import React, { useState, useEffect, useCallback } from 'react';
import './CommissionsOverview.css';
import { money } from '../utils/money';
import CommissionSectionNav from './CommissionSectionNav';

const API = '';

function fmt(val) {
  return money(parseFloat(val) || 0);
}

function pct(val) {
  return (parseFloat(val) * 100).toFixed(2) + '%';
}

function getCommissionTypeLabel(type) {
  const labels = {
    standard_tiered: 'Standard Tiered',
    hunting: 'Hunting',
    rate_on_full_split: 'Rate on Full · 70/30',
    zen_rate_on_full_split: 'Rate on Full · 70/30'
  };
  return labels[type] || type;
}

function getTierRate(tierData = [], revenue = 0) {
  const value = parseFloat(revenue) || 0;
  const tier = tierData.find(item => value >= parseFloat(item.revenue_min)
    && value <= parseFloat(item.revenue_max));
  return tier ? parseFloat(tier.rate) || 0 : 0;
}

function getSchemeStatus(person, tierData) {
  const revenue = parseFloat(person.total_revenue) || 0;
  if (person.commission_type === 'rate_on_full_split'
    || person.commission_type === 'zen_rate_on_full_split') {
    const rate = getTierRate(tierData, revenue);
    return rate > 0 ? `${(rate * 100).toFixed(2)}% tier` : 'No active tier';
  }
  if (revenue <= 0) return 'No revenue';
  return person.meets_floor ? 'Above floor' : 'Below floor';
}

function getMonthIndex(month) {
  return parseInt(month.split('-')[1], 10);
}

const FLOOR_MONTHS = [
  { key: 'floor_may', label: 'May', num: 5 },
  { key: 'floor_jun', label: 'Jun', num: 6 },
  { key: 'floor_jul', label: 'Jul', num: 7 },
  { key: 'floor_aug', label: 'Aug', num: 8 },
  { key: 'floor_sep', label: 'Sep', num: 9 },
  { key: 'floor_oct', label: 'Oct', num: 10 },
  { key: 'floor_nov', label: 'Nov', num: 11 },
  { key: 'floor_dec', label: 'Dec', num: 12 }
];

export default function CommissionsOverview({ month }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeScheme, setActiveScheme] = useState('standard');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/commissions/overview?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch commissions data');
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="commissions-overview">
        <CommissionSectionNav />
        <div className="co-loading">
          <div className="spinner"></div>
          <span>Loading commissions data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="commissions-overview">
        <CommissionSectionNav />
        <div className="co-error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { tiers, w_timber_tiers, booster_rates } = data;
  const salespeople = (data.salespeople || []).filter(person => person.is_active !== false);
  const currentMonthNum = getMonthIndex(month);
  const monthLabel = new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Find people by type
  const ginny = salespeople.find(p => p.name === 'Ginny');
  const zac = salespeople.find(p => p.name === 'Zac');
  const zen = salespeople.find(p => p.name === 'Zen');
  const jq = salespeople.find(p => p.name === 'JQ');
  const huntingPeople = salespeople.filter(p => p.commission_type === 'hunting');
  const floorPeople = salespeople.filter(p => p.commission_type === 'standard_tiered');
  const totals = salespeople.reduce((sum, person) => ({
    revenue: sum.revenue + (parseFloat(person.total_revenue) || 0),
    commission: sum.commission + (parseFloat(person.total_commission) || 0),
    earned: sum.earned + (parseFloat(person.total_earned) || 0)
  }), { revenue: 0, commission: 0, earned: 0 });

  // Compute shifted tiers for display
  function getShiftedTiers(tierData, person) {
    if (!person) return tierData;
    const baseFloor = parseFloat(tierData[0]?.revenue_max) || 0;
    const shift = (person.current_floor || 0) - baseFloor;
    return tierData.map(t => ({
      ...t,
      shifted_min: parseFloat(t.revenue_min) + shift,
      shifted_max: parseFloat(t.revenue_max) + shift
    }));
  }

  function getActiveTierLevel(shiftedTiers, revenue) {
    const rev = parseFloat(revenue) || 0;
    for (let i = shiftedTiers.length - 1; i >= 0; i--) {
      if (rev > shiftedTiers[i].shifted_min) return shiftedTiers[i].tier_level;
    }
    return -1;
  }

  const ginnyTiers = getShiftedTiers(tiers.standard_ginny, ginny);
  const zacTiers = getShiftedTiers(tiers.standard_zac, zac);
  const ginnyActiveTier = getActiveTierLevel(ginnyTiers, ginny?.eligible_revenue);
  const zacActiveTier = getActiveTierLevel(zacTiers, zac?.eligible_revenue);

  return (
    <div className="commissions-overview">
      <CommissionSectionNav />
      <div className="co-summary-heading">
        <div>
          <h2>Monthly results</h2>
          <p>{monthLabel} · {salespeople.length} active salespeople</p>
        </div>
      </div>

      {/* Section 1: compact monthly comparison */}
      <div className="co-summary-panel" aria-label="Salesperson commission summary">
        <div className="co-summary-table-head" aria-hidden="true">
          <span>Salesperson</span>
          <span>Scheme</span>
          <span>Revenue</span>
          <span>Commission</span>
          <span>Total earned</span>
        </div>
        {salespeople.map(p => {
          const revenue = parseFloat(p.total_revenue) || 0;
          const commission = parseFloat(p.total_commission) || 0;
          const totalEarned = parseFloat(p.total_earned) || 0;
          const tierData = p.commission_type === 'rate_on_full_split'
            ? tiers.jq_rate_on_full
            : p.commission_type === 'zen_rate_on_full_split' ? tiers.zen_rate_on_full : [];
          return (
            <div className="co-summary-row" key={p.id}>
              <div className="co-summary-person">
                <span className={`co-summary-avatar person-${p.name.toLowerCase()}`}>{p.name.slice(0, 2)}</span>
                <div>
                  <strong>{p.name}</strong>
                  <span>{p.role}</span>
                </div>
              </div>
              <div className="co-summary-scheme">
                <strong>{getCommissionTypeLabel(p.commission_type)}</strong>
                <span>{getSchemeStatus(p, tierData)}</span>
              </div>
              <div className="co-summary-money revenue">
                <span className="co-mobile-label">Revenue</span>
                <strong>{fmt(revenue)}</strong>
              </div>
              <div className="co-summary-money commission">
                <span className="co-mobile-label">Commission</span>
                <strong>{fmt(commission)}</strong>
              </div>
              <div className="co-summary-money earned">
                <span className="co-mobile-label">Total earned</span>
                <strong>{fmt(totalEarned)}</strong>
              </div>
            </div>
          );
        })}
        <div className="co-summary-total">
          <strong>Team total</strong>
          <span></span>
          <strong>{fmt(totals.revenue)}</strong>
          <strong>{fmt(totals.commission)}</strong>
          <strong>{fmt(totals.earned)}</strong>
        </div>
      </div>

      {/* Section 2: Commission Schemes */}
      <div className="co-section-heading">
        <h3>Scheme details</h3>
        <p>Rates, thresholds and payout rules for each active scheme.</p>
      </div>
      <div className="co-schemes-section">
        <div className="co-scheme-tabs">
          <button className={`co-scheme-tab ${activeScheme === 'standard' ? 'active' : ''}`}
            onClick={() => setActiveScheme('standard')}>Ginny &amp; Zac</button>
          {huntingPeople.length > 0 && <button className={`co-scheme-tab ${activeScheme === 'hunting' ? 'active' : ''}`}
            onClick={() => setActiveScheme('hunting')}>Hunting</button>}
          <button className={`co-scheme-tab ${activeScheme === 'jq' ? 'active' : ''}`}
            onClick={() => setActiveScheme('jq')}>JQ</button>
          <button className={`co-scheme-tab ${activeScheme === 'zen' ? 'active' : ''}`}
            onClick={() => setActiveScheme('zen')}>Zen</button>
        </div>

        {activeScheme === 'standard' && (
          <div className="co-standard-grid">
            {/* Ginny */}
            <div className="co-scheme-card">
              <h4>Standard Tiered - Ginny</h4>
              <p className="co-scheme-subtitle">
                Tiers shift with monthly floor. Band widths: T1-T5: RM 20k, T6-T10: RM 30k, T11-T16: RM 40k.
                First {ginny?.exclude_first_n_deposits || 2} deposits excluded from eligible revenue.
              </p>
              <div className="co-scheme-info-row">
                <div className="co-info-chip">
                  <span className="label">Floor:</span>
                  <span className="value">{fmt(ginny?.current_floor)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Eligible Revenue:</span>
                  <span className="value green">{fmt(ginny?.eligible_revenue)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Tier Commission:</span>
                  <span className="value yellow">{fmt(ginny?.tier_commission)}</span>
                </div>
              </div>
              <div className="co-tier-table-wrap">
                <table className="co-tier-table">
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ginnyTiers.map(t => (
                      <tr key={t.tier_level} className={t.tier_level === ginnyActiveTier ? 'active-tier' : ''}>
                        <td>T{t.tier_level}</td>
                        <td>{fmt(t.shifted_min)}</td>
                        <td>{t.shifted_max >= 999999000 ? 'Unlimited' : fmt(t.shifted_max)}</td>
                        <td className="rate-cell">{pct(t.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Zac */}
            <div className="co-scheme-card">
              <h4>Standard Tiered - Zac</h4>
              <p className="co-scheme-subtitle">
                Tiers shift with monthly floor. Band widths: T1-T5: RM 20k, T6-T10: RM 30k, T11-T16: RM 40k.
                First {zac?.exclude_first_n_deposits || 4} deposits excluded from eligible revenue.
              </p>
              <div className="co-scheme-info-row">
                <div className="co-info-chip">
                  <span className="label">Floor:</span>
                  <span className="value">{fmt(zac?.current_floor)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Eligible Revenue:</span>
                  <span className="value green">{fmt(zac?.eligible_revenue)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Tier Commission:</span>
                  <span className="value yellow">{fmt(zac?.tier_commission)}</span>
                </div>
              </div>
              <div className="co-tier-table-wrap">
                <table className="co-tier-table">
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zacTiers.map(t => (
                      <tr key={t.tier_level} className={t.tier_level === zacActiveTier ? 'active-tier' : ''}>
                        <td>T{t.tier_level}</td>
                        <td>{fmt(t.shifted_min)}</td>
                        <td>{t.shifted_max >= 999999000 ? 'Unlimited' : fmt(t.shifted_max)}</td>
                        <td className="rate-cell">{pct(t.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeScheme === 'hunting' && (
          <div>
            <div className="co-scheme-card">
              <h4>Hunting Commission</h4>
              <p className="co-scheme-subtitle">
                Revenue above the hunting threshold earns commission at a flat rate. No deposit exclusions.
              </p>
              <div className="co-hunting-grid">
                {huntingPeople.map(person => {
                  const rev = parseFloat(person.eligible_revenue) || 0;
                  const threshold = parseFloat(person.hunting_threshold) || 0;
                  const rate = parseFloat(person.hunting_rate) || 0;
                  const excess = Math.max(0, rev - threshold);
                  const progressPct = threshold > 0 ? Math.min(100, (rev / threshold) * 100) : 0;
                  return (
                    <div className="co-hunting-card" key={person.id}>
                      <h5>{person.name} ({person.role})</h5>
                      <div className="co-hunting-stat">
                        <span className="label">Threshold</span>
                        <span className="value">{fmt(threshold)}</span>
                      </div>
                      <div className="co-hunting-stat">
                        <span className="label">Hunting Rate</span>
                        <span className="value blue">{rate}%</span>
                      </div>
                      <div className="co-hunting-stat">
                        <span className="label">Floor ({monthLabel})</span>
                        <span className="value">{fmt(person.current_floor)}</span>
                      </div>
                      <div className="co-hunting-stat">
                        <span className="label">Revenue</span>
                        <span className="value green">{fmt(rev)}</span>
                      </div>
                      <div className="co-hunting-stat">
                        <span className="label">Above Threshold</span>
                        <span className={`value ${excess > 0 ? 'green' : 'yellow'}`}>{fmt(excess)}</span>
                      </div>
                      <div className="co-hunting-stat">
                        <span className="label">Hunting Commission</span>
                        <span className="value yellow">{fmt(person.hunting_commission)}</span>
                      </div>
                      <div className="co-progress-bar">
                        <div
                          className={`co-progress-fill ${rev >= threshold ? 'green' : 'yellow'}`}
                          style={{ width: `${Math.min(progressPct, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeScheme === 'jq' && (
          <div>
            <div className="co-scheme-card">
              <h4>Rate on Full / 70-30 Split - JQ</h4>
              <p className="co-scheme-subtitle">
                Based on manually entered bookings. Commission rate determined by cumulative booked value.
                70% paid on booking month, 30% settled on delivery month.
              </p>
              <div className="co-scheme-info-row">
                <div className="co-info-chip">
                  <span className="label">Floor:</span>
                  <span className="value">{fmt(jq?.current_floor)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Revenue:</span>
                  <span className="value green">{fmt(jq?.total_revenue)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">70% Paid:</span>
                  <span className="value yellow">{fmt(jq?.jq_70_paid)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">30% Settled:</span>
                  <span className="value yellow">{fmt(jq?.jq_30_settlements)}</span>
                </div>
              </div>
              <div className="co-tier-table-wrap">
                <table className="co-tier-table">
                  <thead>
                    <tr>
                      <th>Tier</th>
                      <th>Cumulative From</th>
                      <th>Cumulative To</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiers.jq_rate_on_full.map(t => (
                      <tr key={t.tier_level}>
                        <td>T{t.tier_level}</td>
                        <td>{fmt(t.revenue_min)}</td>
                        <td>{parseFloat(t.revenue_max) >= 9999999 ? 'Unlimited' : fmt(t.revenue_max)}</td>
                        <td className="rate-cell">{pct(t.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeScheme === 'zen' && (
          <div>
            <div className="co-scheme-card">
              <h4>Rate on Full / 70-30 Split - Zen</h4>
              <p className="co-scheme-subtitle">
                Effective August 2026 for Wedding, Private Event and Corporate bookings. One rate applies to the full month's booked value.
                70% counts after verification and the balance settles on delivery using the locked rate. Values above RM449,999 return to 0%.
              </p>
              <div className="co-scheme-info-row">
                <div className="co-info-chip">
                  <span className="label">Threshold:</span>
                  <span className="value">{fmt(200000)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Eligible Booked Value:</span>
                  <span className="value green">{fmt(zen?.eligible_revenue)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">70% Paid:</span>
                  <span className="value yellow">{fmt(zen?.zen_70_paid)}</span>
                </div>
                <div className="co-info-chip">
                  <span className="label">Settlement:</span>
                  <span className="value yellow">{fmt(zen?.zen_30_settlements)}</span>
                </div>
              </div>
              <div className="co-tier-table-wrap">
                <table className="co-tier-table">
                  <thead><tr><th>Tier</th><th>Cumulative From</th><th>Cumulative To</th><th>Rate</th></tr></thead>
                  <tbody>
                    {(tiers.zen_rate_on_full || []).map(t => (
                      <tr key={t.tier_level}>
                        <td>{t.tier_level === 0 ? 'Floor' : `T${t.tier_level}`}</td>
                        <td>{fmt(t.revenue_min)}</td><td>{fmt(t.revenue_max)}</td><td className="rate-cell">{pct(t.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Boosters Reference */}
      <h3 className="co-section-title">Boosters</h3>
      <div className="co-boosters-grid">
        <div className="co-booster-card">
          <h4>Type 1: Wedding/Birthday Deposit Booster</h4>
          <div className="co-booster-who">Applies to: Ginny and Zac</div>
          <table className="co-booster-table">
            <thead>
              <tr>
                <th>Event Month</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {booster_rates.type1.map((b, i) => (
                <tr key={i}>
                  <td>{b.period}</td>
                  <td style={{ color: '#56d364', fontWeight: 600 }}>{(b.rate * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="co-booster-card">
          <h4>Type 2: W Timber Booster (Zac)</h4>
          <div className="co-booster-who">Applies to: Zac only - based on monthly W Timber revenue</div>
          <table className="co-booster-table">
            <thead>
              <tr>
                <th>Revenue From</th>
                <th>Revenue To</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {w_timber_tiers.map(t => (
                <tr key={t.id}>
                  <td>{fmt(t.revenue_min)}</td>
                  <td>{parseFloat(t.revenue_max) >= 999999000 ? 'Unlimited' : fmt(t.revenue_max)}</td>
                  <td style={{ color: '#56d364', fontWeight: 600 }}>{parseFloat(t.rate).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="co-booster-card">
          <h4>Type 2: Event Space 1% (Ginny)</h4>
          <div className="co-booster-who">Applies to: Ginny only - 1% of event space revenue</div>
          <div style={{ background: 'var(--rmc-cream)', borderRadius: '8px', padding: '1rem', marginTop: '0.5rem' }}>
            <div className="co-hunting-stat">
              <span className="label">Rate</span>
              <span className="value blue">{(booster_rates.type2_rate * 100).toFixed(0)}%</span>
            </div>
            <div className="co-hunting-stat">
              <span className="label">Applied on</span>
              <span className="value">Event Space Revenue</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Floor Targets */}
      <div className="co-section-heading">
        <h3>Monthly floors</h3>
        <p>Standard-tiered targets from May to December.</p>
      </div>
      <div className="co-floor-section">
        <div className="co-floor-card">
          <div className="co-tier-table-wrap">
            <table className="co-floor-table">
              <thead>
                <tr>
                  <th>Salesperson</th>
                  {FLOOR_MONTHS.map(fm => (
                    <th key={fm.key} className={fm.num === currentMonthNum ? 'current-month' : ''}>
                      {fm.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {floorPeople.map(p => (
                  <tr key={p.id}>
                    <td className="name-cell">{p.name}</td>
                    {FLOOR_MONTHS.map(fm => (
                      <td key={fm.key} className={fm.num === currentMonthNum ? 'current-month' : ''}>
                        {fmt(p[fm.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
